/** Onboarding checklist — callable wrappers + a shared active-people hook. */
import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { functions, db } from '../lib/firebase';
import {
  CALLABLES,
  type OnboardingCreateRequest,
  type OnboardingCreateResponse,
  type OnboardingGetRequest,
  type OnboardingGetResponse,
  type OnboardingListResponse,
  type OnboardingMineResponse,
  type OnboardingUpdateHeaderRequest,
  type OnboardingUpdateHeaderResponse,
  type OnboardingSetTaskRequest,
  type OnboardingSetTaskResponse,
  type OnboardingAddTaskRequest,
  type OnboardingAddTaskResponse,
  type OnboardingDeleteTaskRequest,
  type OnboardingDeleteTaskResponse,
  type OnboardingSetStatusRequest,
  type OnboardingSetStatusResponse,
  type OnboardingDeleteRequest,
  type OnboardingDeleteResponse,
  type OnboardingSyncTemplateRequest,
  type OnboardingSyncTemplateResponse,
} from '@daaily/shared';

export const onboardingApi = {
  create: httpsCallable<OnboardingCreateRequest, OnboardingCreateResponse>(functions, CALLABLES.ONBOARDING_CREATE),
  get: httpsCallable<OnboardingGetRequest, OnboardingGetResponse>(functions, CALLABLES.ONBOARDING_GET),
  list: httpsCallable<unknown, OnboardingListResponse>(functions, CALLABLES.ONBOARDING_LIST),
  mine: httpsCallable<unknown, OnboardingMineResponse>(functions, CALLABLES.ONBOARDING_MINE),
  updateHeader: httpsCallable<OnboardingUpdateHeaderRequest, OnboardingUpdateHeaderResponse>(functions, CALLABLES.ONBOARDING_UPDATE_HEADER),
  setTask: httpsCallable<OnboardingSetTaskRequest, OnboardingSetTaskResponse>(functions, CALLABLES.ONBOARDING_SET_TASK),
  addTask: httpsCallable<OnboardingAddTaskRequest, OnboardingAddTaskResponse>(functions, CALLABLES.ONBOARDING_ADD_TASK),
  deleteTask: httpsCallable<OnboardingDeleteTaskRequest, OnboardingDeleteTaskResponse>(functions, CALLABLES.ONBOARDING_DELETE_TASK),
  setStatus: httpsCallable<OnboardingSetStatusRequest, OnboardingSetStatusResponse>(functions, CALLABLES.ONBOARDING_SET_STATUS),
  remove: httpsCallable<OnboardingDeleteRequest, OnboardingDeleteResponse>(functions, CALLABLES.ONBOARDING_DELETE),
  syncTemplate: httpsCallable<OnboardingSyncTemplateRequest, OnboardingSyncTemplateResponse>(functions, CALLABLES.ONBOARDING_SYNC_TEMPLATE),
};

export interface ActivePerson {
  email: string;
  displayName: string;
}

/** Live count of the user's OPEN onboarding tasks (as employee/responsible),
 *  summed across active checklists — for the sidebar badge. */
/** Active checklists the user is on (as employee, task owner or added viewer) + their open tasks there. */
export function useMyOnboardingCount(email: string): { open: number; lists: number } {
  const [count, setCount] = useState({ open: 0, lists: 0 });
  useEffect(() => {
    if (!email) { setCount({ open: 0, lists: 0 }); return; }
    const q = query(collection(db, 'onboardingChecklists'), where('responsibleEmails', 'array-contains', email));
    const unsub = onSnapshot(q, (snap) => {
      let n = 0; let lists = 0;
      snap.forEach((d) => {
        const c = d.data() as { status?: string; openCountByEmail?: Record<string, number> };
        if (c.status === 'active') { lists += 1; n += c.openCountByEmail?.[email] ?? 0; }
      });
      setCount({ open: n, lists });
    }, () => setCount({ open: 0, lists: 0 }));
    return () => unsub();
  }, [email]);
  return count;
}

/** Live list of active users (from the public mirror), for the people pickers. */
export function useActivePeople(): ActivePerson[] {
  const [people, setPeople] = useState<ActivePerson[]>([]);
  useEffect(() => {
    const q = query(collection(db, 'userPublic'), where('active', '==', true));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => {
        const v = d.data() as { email?: string; displayName?: string };
        return { email: (v.email ?? d.id).toLowerCase(), displayName: v.displayName ?? d.id };
      });
      list.sort((a, b) => a.displayName.localeCompare(b.displayName));
      setPeople(list);
    });
    return () => unsub();
  }, []);
  return people;
}
